// Affiliate programs: AFFCatalog, WhoAff and the programs' own sites, each
// source kept with its own terms and date. A filter must hold inside one
// source's claim — terms are never stitched together across sources.
import { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import { getRadarAffiliates, type AffiliateProgram } from '@/api/radar';
import {
  AFFILIATE_FIELD_FILTERS, EMPTY_AFFILIATE_FILTERS, downloadJson, hostOf, matchesAffiliate, fmtDate, safeHttps,
  type AffiliateFilters,
} from './logic';
import { LoadError, Loading, Pager, SelectInput, TextInput } from './controls';

const PAGE_SIZE = 12;
const RATE_KEYS = ['CPA', 'RevShare', 'Hybrid', 'Caps'];
const KIND_LABEL: Record<string, string> = {
  program: 'Affiliate program', network: 'Affiliate network', advertiser: 'Direct advertiser',
};

export function AffiliatesTab() {
  const data = useQuery({ queryKey: ['radar', 'affiliates'], queryFn: getRadarAffiliates, staleTime: Infinity });
  const [f, setF] = useState<AffiliateFilters>(EMPTY_AFFILIATE_FILTERS);
  const [page, setPage] = useState(1);
  const topRef = useRef<HTMLDivElement>(null);

  const programs = useMemo(() => data.data?.programs ?? [], [data.data]);
  const options = useMemo(() => {
    const claims = programs.flatMap((p) => p.claims);
    const uniq = (values: (string | undefined | null)[]) => [...new Set(values.filter((v): v is string => !!v))].sort();
    return {
      geo: uniq(claims.flatMap((c) => c.geo?.target ?? [])),
      source: uniq(claims.map((c) => hostOf(c.source_url))),
      vertical: uniq(claims.flatMap((c) => c.verticals ?? [])),
      traffic: uniq(claims.flatMap((c) => c.traffic ?? [])),
      ...Object.fromEntries(AFFILIATE_FIELD_FILTERS.map(([k, field]) => [k, uniq(claims.map((c) => c.fields?.[field]))])) as Record<(typeof AFFILIATE_FIELD_FILTERS)[number][0], string[]>,
    };
  }, [programs]);
  const filtered = useMemo(() => programs.filter((p) => matchesAffiliate(p, f)), [programs, f]);
  const official = useMemo(() => programs.filter((p) => p.claims.some((c) => c.verification === 'official_site')).length, [programs]);
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, pages);

  const set = <K extends keyof AffiliateFilters>(key: K, value: AffiliateFilters[K]) => { setF((x) => ({ ...x, [key]: value })); setPage(1); };

  if (data.isLoading) return <Loading what="affiliate programs" />;
  if (data.error) return <LoadError error={data.error} />;

  return (
    <div className="space-y-5">
      <p className="text-sm text-content-muted max-w-4xl">
        GEOs, commissions and terms from public sources. An official site confirms the terms were published, not that the offer is approved for your traffic.
        "Up to" is an advertised maximum; no caps means "not published". A brand + GEO + traffic + rate combination needs separate confirmation.
      </p>

      <section className="bg-surface border border-border rounded-lg p-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-[2fr_1fr_1fr_1fr_auto] items-end">
        <TextInput label="Program or brand" type="search" value={f.q} onChange={(v) => set('q', v)} placeholder="V.Partners, 888STARZ…" />
        <SelectInput label="Target GEO" value={f.geo} onChange={(v) => set('geo', v)} all="All countries" options={options.geo} />
        <SelectInput label="Evidence" value={f.proof} onChange={(v) => set('proof', v)} all="All sources" options={[['official_site', 'Has an official source'], ['directory_claim', 'Catalogs only']]} />
        <SelectInput label="Model" value={f.model} onChange={(v) => set('model', v)} all="All" options={['CPA', 'RevShare', 'Hybrid']} />
        <Button variant="secondary" onClick={() => downloadJson('affiliate-programs.json', filtered)}>↓ JSON</Button>
      </section>

      <details open className="bg-surface border border-border rounded-lg p-4">
        <summary className="cursor-pointer font-medium text-content">More filters</summary>
        <div className="grid gap-3 mt-3 sm:grid-cols-2 lg:grid-cols-4">
          <SelectInput label="Entry type" value={f.kind} onChange={(v) => set('kind', v)} all="All" options={Object.entries(KIND_LABEL)} />
          <SelectInput label="Source" value={f.source} onChange={(v) => set('source', v)} all="All" options={options.source} />
          <SelectInput label="Vertical" value={f.vertical} onChange={(v) => set('vertical', v)} all="All" options={options.vertical} />
          <SelectInput label="Traffic source" value={f.traffic} onChange={(v) => set('traffic', v)} all="All" options={options.traffic} />
          <SelectInput label="License (per source)" value={f.license} onChange={(v) => set('license', v)} all="All" options={options.license} />
          <SelectInput label="Payout frequency" value={f.frequency} onChange={(v) => set('frequency', v)} all="All" options={options.frequency} />
          <SelectInput label="Negative carryover" value={f.negative} onChange={(v) => set('negative', v)} all="All" options={options.negative} />
          <SelectInput label="Admin Fee" value={f.fee} onChange={(v) => set('fee', v)} all="All" options={options.fee} />
          <SelectInput label="Caps published" value={f.caps} onChange={(v) => set('caps', v)} all="All" options={[['yes', 'Yes'], ['no', 'Not published']]} />
          <SelectInput label="Target GEOs listed" value={f.known} onChange={(v) => set('known', v)} all="All" options={[['yes', 'Yes'], ['no', 'No']]} />
          <SelectInput label="GEO conflict" value={f.conflict} onChange={(v) => set('conflict', v)} all="All" options={[['yes', 'Found'], ['no', 'None found']]} />
          <TextInput label="RS: claimed % from" type="number" min={0} max={100} value={f.rs} onChange={(v) => set('rs', v)} placeholder="e.g. 40" />
          <SelectInput label="CPA currency" value={f.currency} onChange={(v) => set('currency', v)} all="All" options={['EUR', 'USD', 'GBP']} />
          <TextInput label="CPA: claimed maximum from" type="number" min={0} value={f.cpa} onChange={(v) => set('cpa', v)} placeholder={f.currency ? 'Amount' : 'Pick a currency first'} disabled={!f.currency} />
          <div className="self-end">
            <Button variant="secondary" onClick={() => { setF(EMPTY_AFFILIATE_FILTERS); setPage(1); }}>Reset filters</Button>
          </div>
        </div>
        <p className="text-xs text-content-subtle mt-3">
          All filter conditions must hold within one source. The CPA and RS thresholds compare published values, including "up to" maximums, not a guaranteed rate.
          CPA is compared only within the chosen currency.
        </p>
      </details>

      <p ref={topRef} role="status" className="text-sm text-content-muted scroll-mt-4">
        {filtered.length} of {programs.length} programs · {official} with official sources
      </p>

      <div className="grid gap-4 lg:grid-cols-2">
        {filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE).map((p) => <ProgramCard key={p.id} program={p} />)}
      </div>
      {!filtered.length && <p className="text-sm text-content-subtle">No programs match these filters.</p>}
      <Pager page={current} pages={pages} onPage={(n) => { setPage(n); topRef.current?.scrollIntoView({ behavior: 'smooth' }); }} />
    </div>
  );
}

function ProgramCard({ program: p }: { program: AffiliateProgram }) {
  return (
    <article className="bg-surface border border-border rounded-lg p-4 space-y-3 min-w-0">
      <div>
        <h3 className="font-medium text-content text-lg break-words">{p.name}</h3>
        <span className="text-xs text-content-subtle">{KIND_LABEL[p.kind] ?? p.kind}</span>
        {p.website && (
          <a href={safeHttps(p.website)} target="_blank" rel="noreferrer" className="block text-sm text-primary hover:underline">Program website ↗</a>
        )}
      </div>
      {p.claims.map((c) => (
        <section key={c.source_url} className="border-t border-border pt-3 space-y-2 text-sm">
          <div>
            <strong className={c.verification === 'official_site' ? 'text-success' : 'text-content'}>
              {c.verification === 'official_site' ? '● Official site' : '◇ Catalog data'}
            </strong>
            <p className="text-xs text-content-subtle">
              {fmtDate(c.observed_at)}{c.source_updated ? ` · source updated: ${c.source_updated}` : ''}
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {RATE_KEYS.map((k) => (
              <div key={k} className="bg-surface-muted rounded-md px-2 py-1.5">
                <p className="text-[11px] text-content-subtle">{k}</p>
                <p className="font-medium text-content break-words">{c.fields?.[k] || 'Not published'}</p>
              </div>
            ))}
          </div>
          <p><span className="font-medium">Target GEOs:</span> {c.geo?.target?.join(', ') || 'Not confirmed by this source'}</p>
          {!!c.conflicts?.length && (
            <p className="text-warning">GEO conflict: {c.conflicts.join(', ')} listed as both targeted and forbidden</p>
          )}
          <details>
            <summary className="cursor-pointer text-content-muted">Restrictions and other details</summary>
            <div className="mt-2 space-y-1">
              <p><span className="font-medium">Forbidden GEOs:</span> {c.geo?.forbidden?.join(', ') || 'Not published'}</p>
              {Object.entries(c.fields ?? {}).filter(([k]) => !RATE_KEYS.includes(k)).map(([k, v]) => (
                <p key={k}><span className="font-medium">{k}:</span> {v}</p>
              ))}
              {c.overview && <p className="text-content-muted">{c.overview}</p>}
            </div>
          </details>
          <a href={safeHttps(c.source_url)} target="_blank" rel="noreferrer" className="text-primary hover:underline">Source ↗</a>
        </section>
      ))}
    </article>
  );
}
